import { useMemo } from 'react';
import { useEChart } from './useEChart.js';
import { ChartFrame } from './ChartFrame.js';
import { formatChartAmount, formatCountFa } from './formatters.js';
import type { ChartBaseStateProps, ChartDatum } from './types.js';

export interface BarChartProps extends ChartBaseStateProps {
  readonly data: readonly ChartDatum[];
  readonly ariaLabel: string;
  readonly height?: number | undefined;
  readonly valueFormatter?: ((value: number) => string) | undefined;
}

/**
 * Single-hue magnitude-over-category chart (a monthly total, a per-status
 * count) — one series needs no legend, per the dataviz skill: the card
 * title already names what's plotted. `--sk-chart-1` (brand-500), 24px
 * bar cap, 4px rounded top.
 */
export function BarChart({
  data,
  ariaLabel,
  height = 260,
  amountUnit,
  valueFormatter: valueFormatterProp,
  isLoading,
  loadingMessage,
  error,
  status,
  errorMessage,
  onRetry,
  retryLabel,
  emptyMessage,
}: BarChartProps) {
  // Stable identity: a fresh default function on every render is a changed
  // `useEChart` input and would repaint (and re-animate) the chart each time.
  const defaultFormatter = useMemo(
    () => (amountUnit ? (v: number) => formatChartAmount(v, amountUnit) : formatCountFa),
    [amountUnit],
  );
  const valueFormatter = valueFormatterProp ?? defaultFormatter;
  const hasError = Boolean(error || (status && status >= 400));
  const isEmpty =
    !isLoading && !hasError && (data.length === 0 || data.every((d) => d.value === 0));
  const containerRef = useEChart(
    (theme) => ({
      grid: { left: 8, right: 12, top: 20, bottom: 28, containLabel: true },
      xAxis: {
        type: 'category',
        data: data.map((d) => d.label),
        axisLine: { lineStyle: { color: theme.grid } },
        axisTick: { show: false },
        axisLabel: { color: theme.textMuted, fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: theme.grid } },
        axisLabel: { color: theme.textMuted, fontSize: 11, formatter: valueFormatter },
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: number) => valueFormatter(Number(v)),
        axisPointer: { type: 'shadow' },
      },
      series: [
        {
          type: 'bar',
          data: data.map((d) => d.value),
          barMaxWidth: 24,
          itemStyle: { color: theme.categorical[0], borderRadius: [4, 4, 0, 0] },
        },
      ],
    }),
    [data, valueFormatter],
  );

  return (
    <ChartFrame
      containerRef={containerRef}
      height={height}
      ariaLabel={ariaLabel}
      isLoading={isLoading}
      loadingMessage={loadingMessage}
      error={error}
      status={status}
      errorMessage={errorMessage}
      onRetry={onRetry}
      retryLabel={retryLabel}
      isEmpty={isEmpty}
      emptyMessage={emptyMessage}
    />
  );
}
